import { useEffect, useState } from "react";
import UserIdentitiesService, {
  AccessRequestDto,
  UserIdentityRequestStatusEnum,
  UserIdentityType,
} from "../../../services/UserIdentitiesService";
import {
  DefaultButton,
  IconButton,
  IIconProps,
  Modal,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";
import React from "react";
import SignaturesService, {
  ISignature,
} from "../../../services/SignaturesService";
import Auth from "../../../Auth/Auth";
import {
  ModalRequestAuthorizationTable,
  SignatureComponentWrapper,
} from "./ModalRequestAuthorization.style";
import moment from "moment";
import StatusPill, { getStatus } from "../../StatusPill/StatusPill";
import { SignatureComponent } from "../../Signature/SignatureComponent";
import { useDispatch } from "react-redux";
import { updateIdentityAccessRequest } from "../../../store/auth/actions";

type Props = {
  showModal: boolean;
  selectedUserIdentity: UserIdentityType;
  handleSubmit?: (
    signatureSuccess: boolean,
    updatedRequest: AccessRequestDto | null
  ) => any;
  userIdentitiesAccessRequest: AccessRequestDto[];
  userId: number;
  isLoading: boolean;
};

const ModalRequestAuthorization = (props: Props) => {
  const userId = Auth.getUserProfile().id;

  const {
    showModal,
    handleSubmit,
    userIdentitiesAccessRequest,
    selectedUserIdentity,
    isLoading,
  } = props;

  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const dispatch = useDispatch();
  // ACCESS-REQUEST
  const [accessRequest, setAccessRequest] = useState<AccessRequestDto[]>(
    userIdentitiesAccessRequest
  );
  const cancelIcon: IIconProps = { iconName: "Cancel" };

  // Firma
  const [signatureId, setSignatureId] = useState<number>();
  const [isSignatureLoading, setIsSignatureLoading] = useState(false);
  const [signatures, setSignatures] = React.useState<ISignature[]>([]);
  const [isSignSubmitting, setIsSignSubmitting] = useState(false);
  const [isSignSubmitted, setIsSignSubmitted] = React.useState<boolean>(false);
  const [isLoadedSignature, setIsLoadedSignature] = useState(false);
  const [alreadySignedText, setAlreadySignedText] = React.useState<string>(
    "Operación firmada con éxito."
  );
  const [validatingAcceptSignature, setValidatingAcceptSignature] =
    useState<boolean>(false);
  const [validatingRejectSignature, setValidatingRejectSignature] =
    useState<boolean>(false);
  const [showSignatureComponent, setShowSignatureComponent] =
    useState<boolean>(false);

  // activar/desactivo los botones de aceptar y rechazar
  const [disabledAcceptButton, setDisabledAcceptButton] =
    useState<boolean>(false);
  const [disabledRejectButton, setDisabledRejectButton] =
    useState<boolean>(false);

  // modal
  const handleCloseModalExistingAccount = () => {
    props.handleSubmit?.(true, null);
  };

  /*****************************************************************************
   * Estado de los botones para aceptar o rechazar la solicitud de autorización
   *****************************************************************************/
  useEffect(() => {
    if (userIdentitiesAccessRequest.length > 0) {
      setAccessRequest(userIdentitiesAccessRequest);

      switch (userIdentitiesAccessRequest[0].status) {
        case UserIdentityRequestStatusEnum.Rejected:
          setDisabledAcceptButton(true);
          setDisabledRejectButton(true);
          break;
        case UserIdentityRequestStatusEnum.Approved:
          setDisabledAcceptButton(true);
          setDisabledRejectButton(false);
          break;
        case UserIdentityRequestStatusEnum.Pending:
          setDisabledAcceptButton(false);
          setDisabledRejectButton(false);
          break;
      }
    }
  }, [userIdentitiesAccessRequest]);

  // firma
  const createSignature = () => {
    setIsLoadedSignature(false);
    SignaturesService.createSignatureNoPlan(userId)
      .then((response: any) => {
        if (response.data.id !== null) {
          setSignatures([response.data]);
          setAlreadySignedText("Firma creada.");
        }
      })
      .catch((error: any) => {
        setAlreadySignedText(
          "Se ha producido un error al firmar el contrato. Por favor, inténtelo de nuevo más tarde."
        );
      })
      .finally(() => {
        setValidatingAcceptSignature(false);
        setIsLoadedSignature(true);
      });
  };

  const onRejectAccessRequestSubmit = async () => {
    if (!accessRequest[0]) return;

    const updatedAccessRequest = {
      ...accessRequest[0],
      status: UserIdentityRequestStatusEnum.Rejected,
      approvalDate: new Date().toISOString(),
    };

    setIsStatusLoading(true);
    setDisabledAcceptButton(true);
    setDisabledRejectButton(true);

    await new Promise((resolve) => setTimeout(resolve, 50));

    try {
      setIsUpdating(true);
      await UserIdentitiesService.patchAccessRequestStatus(
        updatedAccessRequest
      );

      setAccessRequest([updatedAccessRequest]);

      const currentRequests = userIdentitiesAccessRequest || [];
      const newAccessRequestArray = [updatedAccessRequest].filter(
        (req) => req.status === UserIdentityRequestStatusEnum.Pending
      );
      dispatch(
        updateIdentityAccessRequest({
          status: 200,
          count: newAccessRequestArray.length,
          data: newAccessRequestArray,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      setIsUpdating(false);
      setIsStatusLoading(false);
      setDisabledAcceptButton(false);
      setDisabledRejectButton(false);
    }
  };

  const handleSignSubmit = async (response: any) => {
    if (response && response.status === 200) {
      const sentData = JSON.parse(response.config.data);
      const signatureId = sentData[0]?.Id;

      if (!signatureId) return;
      setIsSignSubmitted(true);
      setSignatureId(signatureId);

      await onAcceptAccessRequestSubmit(signatureId);
    }
  };

  const onAcceptAccessRequestSubmit = async (signatureId: number) => {
    if (!accessRequest[0]) return;

    const updatedAccessRequest: AccessRequestDto = {
      ...accessRequest[0],
      status: UserIdentityRequestStatusEnum.Approved,
      sign: signatureId,
      approvalDate: new Date().toISOString(),
    };

    setIsStatusLoading(true);
    setDisabledAcceptButton(true);
    setDisabledRejectButton(true);

    try {
      setIsUpdating(true);
      // patch en base de datos
      await UserIdentitiesService.patchAccessRequestStatus(
        updatedAccessRequest
      );

      // actualizar estado local
      setAccessRequest([updatedAccessRequest]);

      // actualizar redux usando el estado local actualizado
      const newAccessRequestArray = [updatedAccessRequest].filter(
        (req) => req.status === UserIdentityRequestStatusEnum.Pending
      );

      dispatch(
        updateIdentityAccessRequest({
          status: 200,
          count: newAccessRequestArray.length,
          data: newAccessRequestArray,
        })
      );

      props.handleSubmit?.(false, updatedAccessRequest);
    } catch (error) {
      console.error(error);
      props.handleSubmit?.(false, null);
    } finally {
      setIsUpdating(false);
      setIsStatusLoading(false);
      setDisabledAcceptButton(false);
      setDisabledRejectButton(false);
    }
  };

  const processExpirationDate = (signs: ISignature[]) => {
    if (signs !== undefined && signs.length > 0) {
      // eslint-disable-next-line array-callback-return
      signs.map((signature: ISignature) => {
        if (signature && typeof signature.expiresAt === "string") {
          signature.expiresAt = moment(signature.expiresAt).toDate();
        }
      });
    }
  };

  return (
    <>
      <Modal
        isOpen={showModal}
        onDismiss={handleCloseModalExistingAccount}
        isBlocking={false}
        containerClassName="modal-container-RequestAuthorization"
      >
        <div style={{ fontFamily: "Barlow", fontSize: "14px" }}>
          <div
            style={{ width: "100%", marginBottom: "20px", textAlign: "right" }}
          >
            <b style={{ float: "left", fontSize: "24px" }}>
              Estado de solicitudes de acceso
            </b>
            <IconButton
              iconProps={cancelIcon}
              ariaLabel="Cerrar modal"
              onClick={handleCloseModalExistingAccount}
            />
          </div>
          <div>
            <div
              style={{ width: "100%", overflow: "hidden", marginTop: "10px" }}
            >
              <ModalRequestAuthorizationTable>
                <tbody>
                  <tr>
                    <th>Solicitante</th>
                    <th>Interviniente</th>
                    <th>Estado</th>
                    <th>Fecha del último estado</th>
                    <th className="actions"></th>
                  </tr>

                  {isStatusLoading ? (
                    <tr>
                      <td
                        colSpan={5}
                        style={{ textAlign: "center", padding: "20px 0" }}
                      >
                        <Spinner size={SpinnerSize.large} />
                      </td>
                    </tr>
                  ) : accessRequest.length > 0 ? (
                    accessRequest.map((req) => (
                      <tr key={req.id}>
                        <td>
                          {req.requestingUserIdentity?.firstName ?? ""}{" "}
                          {req.requestingUserIdentity?.lastName ?? ""}
                        </td>
                        <td>
                          {selectedUserIdentity?.firstName ?? ""}{" "}
                          {selectedUserIdentity?.lastName ?? ""}
                        </td>
                        <td>
                          <StatusPill
                            status={getStatus(req.status)}
                            type="small"
                          />
                        </td>
                        <td>
                          {req.updatedAt
                            ? moment(req.updatedAt).format("DD/MM/YYYY")
                            : ""}
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              justifyContent: "end",
                            }}
                          >
                            <DefaultButton
                              className="button mini-primary-button"
                              disabled={
                                disabledAcceptButton ||
                                isSignSubmitting ||
                                isUpdating
                              }
                              style={{ minWidth: "120px" }}
                              onClick={() => {
                                setShowSignatureComponent(true);
                                setIsSignSubmitting(true);
                                createSignature(); // solo prepara la firma
                                setValidatingAcceptSignature(true);
                              }}
                            >
                              Aceptar
                              {isUpdating && (
                                <Spinner size={SpinnerSize.small} />
                              )}
                            </DefaultButton>

                            <DefaultButton
                              className="button mini-primary-button"
                              disabled={
                                disabledRejectButton ||
                                isSignSubmitting ||
                                isUpdating
                              }
                              style={{ minWidth: "120px" }}
                              onClick={onRejectAccessRequestSubmit}
                            >
                              Rechazar
                              {isUpdating && (
                                <Spinner size={SpinnerSize.small} />
                              )}
                            </DefaultButton>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        style={{ textAlign: "center", padding: "20px 0" }}
                      >
                        No hay solicitudes de acceso
                      </td>
                    </tr>
                  )}
                </tbody>
              </ModalRequestAuthorizationTable>
            </div>
          </div>
          <div>
            {showSignatureComponent ? (
              isSignatureLoading || signatures.length === 0 ? (
                // Spinner mientras se crea o carga la firma
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <Spinner size={SpinnerSize.large} />
                </div>
              ) : (
                <SignatureComponentWrapper>
                  <SignatureComponent
                    signatures={signatures}
                    submitButtonText="Firmar solicitud"
                    headerTextArea="Recibirás un SMS en el teléfono con la clave para firmar esta operación."
                    cancelButtonText=""
                    alreadySignedText={alreadySignedText}
                    backButtonText="Volver"
                    requestNewCodeButtonText="Recibir nuevo código"
                    onCancelButtonClicked={() => {
                      setIsSignSubmitting(false);
                      setShowSignatureComponent(false);
                    }}
                    onBackButtonClicked={() => setIsSignSubmitting(false)}
                    onNewCodeSended={(updatedSignatures) => {
                      processExpirationDate(updatedSignatures);
                      setSignatures(updatedSignatures);
                    }}
                    onSignatureChanged={(updatedSignatures) => {
                      processExpirationDate(updatedSignatures);
                      setSignatures(updatedSignatures);
                    }}
                    onSigned={handleSignSubmit}
                  />
                </SignatureComponentWrapper>
              )
            ) : null}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ModalRequestAuthorization;
